/**
 * MyApplicationsUtils
 * -------------------
 * Self-contained ownership logic for the "My Published Applications" section of
 * the My Page view. Kept separate from Apps.jsx / AppActivityUtils.js so it can
 * be extended to additional alias types (or reused elsewhere) without touching
 * the shared marketplace code.
 *
 * Approach (option B — same technique as the "View as" filter in Apps.jsx):
 * for a given app owner alias we fetch that owner's *supervisor chain* (the
 * people above them) live from PAPI via `getEmployeeDetailsByLogin`, and check
 * whether the current user's alias appears in it. If the current user is in an
 * owner's supervisor chain, that owner is a direct/indirect report of the user,
 * so the app qualifies. A plain `alias === me` check covers the user's own apps.
 *
 * This is directional: it matches apps owned by the user or by anyone who rolls
 * up to the user. It never matches apps owned by the user's own managers/peers.
 *
 * No recursion and no per-report expansion: one cached supervisor-chain lookup
 * per distinct owner alias.
 */

import { API, graphqlOperation } from "aws-amplify";
import { TEMPLATE_APP, AI_APP_SPECIFIC_FIELDS } from "src/Ateam-BI/Templates/Constants";

/**
 * Max in-flight PAPI (getEmployeeDetailsByLogin) lookups. PAPI throttles at
 * ~50 TPS PER REGION, app-wide (CoralThrottle token bucket, evaluated per
 * second), shared across every PAPI caller in the app. We keep a single user
 * session's burst comfortably under that while still resolving a large owner
 * set reasonably fast. Only DISTINCT owner aliases are looked up (deduped +
 * cached), so total calls stay bounded; the 429-aware jittered backoff in
 * getEmployeeInfo recovers if a burst does clip the limit.
 */
export const PAPI_LOOKUP_CONCURRENCY = 20;

/**
 * Alias-type fields on an app that participate in ownership.
 *
 * To support a new alias type, add a single entry here — nothing else in this
 * module needs to change. `field` is the key on the app object returned by
 * `fetchApps` (values may be a string or an array of strings).
 */
export const OWNERSHIP_ALIAS_TYPES = [
  { key: "app_owner", label: "App Builder", field: AI_APP_SPECIFIC_FIELDS.APP_OWNER },
  { key: "grc_control_owner", label: "GRC Control Owner", field: TEMPLATE_APP.GRC_CONTROL_OWNER },
  { key: "process_owner", label: "Process Owner", field: TEMPLATE_APP.PROCESS_OWNER },
];

/**
 * Normalize an app field that may be a string, array, or null into a clean
 * array of non-empty alias strings.
 */
const toAliasList = (value) => {
  if (value == null) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr
    .filter((a) => typeof a === "string")
    .map((a) => a.trim())
    .filter((a) => a !== "");
};

/**
 * Fetch a login's supervisor chain (comma-joined lowercase logins), memoized in
 * the provided cache object to avoid duplicate PAPI calls within a page load.
 *
 * @param {string} login   owner alias to look up
 * @param {Object} cache   plain object used as a { login: chainString } cache
 * @returns {Promise<string>} comma-separated lowercase supervisor logins, or ""
 */
export const getSupervisorChain = async (login, cache = {}) => {
  const info = await getEmployeeInfo(login, cache);
  return info.chainString;
};

/**
 * Build a human-friendly display name from a PAPI `basicInfo` object, falling
 * back to the login when names are missing.
 */
const buildDisplayName = (basicInfo, login) => {
  const first = (basicInfo?.firstName || "").trim();
  const last = (basicInfo?.lastName || "").trim();
  const full = `${first} ${last}`.trim();
  return full || login;
};

/**
 * Fetch and cache an employee's display info + supervisor chain in one PAPI
 * lookup. Serves both the ownership check (via `chainString`) and the team-view
 * enrichment (name/title/relationship). Cached per login.
 *
 * @param {string} login   employee alias
 * @param {Object} cache   shared cache: { login: EmployeeInfo }
 * @returns {Promise<{
 *   login: string, name: string, businessTitle: string,
 *   chain: {login: string, name: string}[], chainString: string
 * }>}
 */
export const getEmployeeInfo = async (login, cache = {}) => {
  const key = (login || "").toLowerCase();
  const empty = { login: key, name: login || "", businessTitle: "", chain: [], chainString: "" };
  if (!key) return empty;
  if (Object.prototype.hasOwnProperty.call(cache, key)) return cache[key];

  // Retry only *transient* failures (network/throttle) a couple of times with a
  // short backoff. A definitive "no data" response for an alias (PAPI has no
  // record, or the resolver returned null) is not transient — retrying wastes
  // calls, so we cache the fallback and move on. In all cases the UI degrades
  // gracefully to showing the alias, so failures are logged as warnings.
  const maxAttempts = 3;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const details = await API.graphql(
        graphqlOperation(`
          query MyQuery {
            getEmployeeDetailsByLogin(login: "${login}")
          }
        `)
      );
      const raw = details?.data?.getEmployeeDetailsByLogin;

      // Definitive empty/no-data response — not a transient error. Cache the
      // alias-only fallback so we don't repeatedly re-query a login PAPI can't
      // resolve, and stop retrying.
      if (raw == null || raw === "") {
        cache[key] = empty;
        return empty;
      }

      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      // supervisorChain is ordered from immediate manager upward.
      const chain = (data?.supervisorChain || [])
        .filter((s) => s?.login)
        .map((s) => ({
          login: String(s.login).toLowerCase(),
          name: buildDisplayName(s?.basicInfo, s.login),
        }));
      const info = {
        login: key,
        name: buildDisplayName(data?.basicInfo, login),
        businessTitle: (data?.basicInfo?.businessTitle || "").trim(),
        chain,
        chainString: chain.map((c) => c.login).join(","),
      };
      cache[key] = info;
      return info;
    } catch (error) {
      if (attempt === maxAttempts) {
        // The UI degrades to showing the alias, so this is a warning, not an
        // error. Do NOT cache — allow a later retry (next render/refresh).
        console.warn(
          `getEmployeeInfo: could not resolve "${login}" after ${maxAttempts} attempts; falling back to alias.`,
          error?.errors?.[0]?.message || error?.message || error
        );
        return empty;
      }
      // PAPI throttles at ~50 TPS app-wide (CoralThrottle token bucket). A 429
      // needs a longer, jittered backoff so the retry lands after the per-second
      // window resets rather than immediately re-flooding. Other transient
      // errors (5xx) use a shorter backoff.
      const isThrottled = is429Error(error);
      const base = isThrottled ? 600 : 150;
      const backoff = base * 2 ** (attempt - 1); // 429: 600/1200; else 150/300
      const jitter = Math.floor(Math.random() * 250);
      await new Promise((r) => setTimeout(r, backoff + jitter));
    }
  }
  return empty;
};

/**
 * Detect a PAPI 429 (Too Many Requests) from an AppSync/GraphQL error. AppSync
 * surfaces the underlying HTTP error in the errors[].message text.
 */
const is429Error = (error) => {
  const msg =
    error?.errors?.[0]?.message || error?.message || String(error || "");
  return /\b429\b/.test(msg) || /too many requests/i.test(msg);
};

/**
 * Determine an owner's reporting relationship to the current user.
 *
 * Uses the owner's supervisor chain (immediate manager first):
 *   - Direct   : the user is the owner's immediate manager (chain[0]).
 *   - Indirect : the user is higher up the chain; `via` is the report just
 *                below the user in that chain (the owner's ancestor toward you).
 *   - null     : the user is not above this owner at all.
 *
 * @param {string} ownerLogin  owner alias
 * @param {string} userAlias   current user's alias
 * @param {Object} cache       shared employee-info cache
 * @returns {Promise<null | { type: "Direct" | "Indirect", via: string|null, viaName: string|null }>}
 */
export const getReportRelationship = async (ownerLogin, userAlias, cache = {}) => {
  const me = (userAlias || "").toLowerCase();
  const info = await getEmployeeInfo(ownerLogin, cache);
  const chain = info.chain;
  const meIndex = chain.findIndex((c) => c.login === me);
  if (meIndex < 0) return null; // user is not above this owner
  if (meIndex === 0) {
    return { type: "Direct", via: null, viaName: null };
  }
  // The person immediately below the user in the chain is who the owner
  // reports up through toward the user.
  const via = chain[meIndex - 1];
  let viaName = via.name;
  // Chain entries often carry only a login (no basicInfo), so `via.name` may be
  // the alias. Resolve a real display name for the "via" person if needed.
  if (!viaName || viaName.toLowerCase() === via.login) {
    const viaInfo = await getEmployeeInfo(via.login, cache);
    viaName = viaInfo.name || via.login;
  }
  return { type: "Indirect", via: via.login, viaName };
};

/**
 * Does `userAlias` own, or sit above the owner of, this app for a single alias
 * type field?
 *
 * @param {string[]} owners     owner aliases for one alias type
 * @param {string}   userAlias  current user's alias (lowercased)
 * @param {Object}   cache      supervisor-chain cache
 * @returns {Promise<boolean>}
 */
const matchesAliasType = async (owners, userAlias, cache) => {
  // Direct ownership.
  if (owners.some((o) => o.toLowerCase() === userAlias)) return true;

  // Indirect: user is somewhere in an owner's supervisor chain.
  for (const owner of owners) {
    const chain = await getSupervisorChain(owner, cache);
    if (chain && chain.split(",").includes(userAlias)) {
      return true;
    }
  }
  return false;
};

/**
 * Determine whether an app should appear in the current user's "My Published
 * Applications" — i.e. the user's alias, or any of their direct/indirect
 * reports, is an App Builder, GRC Control Owner, or Process Owner of the app.
 *
 * @param {Object} app        app object from fetchApps
 * @param {string} userAlias  current user's alias
 * @param {Object} cache      supervisor-chain cache shared across apps
 * @returns {Promise<boolean>}
 */
export const isPublishedByUserOrReports = async (app, userAlias, cache = {}) => {
  const me = (userAlias || "").toLowerCase();
  if (!me) return false;

  for (const aliasType of OWNERSHIP_ALIAS_TYPES) {
    const owners = toAliasList(app?.[aliasType.field]);
    if (owners.length === 0) continue;
    // eslint-disable-next-line no-await-in-loop
    if (await matchesAliasType(owners, me, cache)) return true;
  }
  return false;
};

/**
 * Filter a list of apps down to those the user has "published" (owns directly or
 * via a direct/indirect report). Evaluated concurrently across apps; the shared
 * cache guarantees each distinct owner alias is only resolved once.
 *
 * @param {Object[]} apps       apps from fetchApps
 * @param {string}   userAlias  current user's alias
 * @param {Object}   cache      optional supervisor-chain cache to reuse
 * @returns {Promise<Object[]>} apps that qualify, order preserved
 */
export const filterMyPublishedApps = async (apps = [], userAlias, cache = {}) => {
  if (!userAlias || apps.length === 0) return [];
  const ownerMatches = await resolveOwnerMatchSet(apps, userAlias, cache);
  return apps.filter((app) => appMatchesOwnerSet(app, ownerMatches));
};

/**
 * Collect the DISTINCT owner aliases across all apps (all ownership fields),
 * then resolve each once — with bounded concurrency — into a Set of owners that
 * the user owns directly or sits above. This decouples the (expensive) PAPI
 * chain lookups from the app list: instead of re-deciding per app, we resolve
 * the unique owner universe a single time, keeping the concurrency pool fully
 * saturated and never looking up the same alias twice.
 *
 * @returns {Promise<Set<string>>} lowercased owner aliases that "belong to" the user
 */
export const resolveOwnerMatchSet = async (apps = [], userAlias, cache = {}) => {
  const me = (userAlias || "").toLowerCase();
  const matched = new Set();
  if (!me) return matched;

  // Distinct owner aliases across every ownership field.
  const distinctOwners = new Set();
  apps.forEach((app) => {
    OWNERSHIP_ALIAS_TYPES.forEach((aliasType) => {
      toAliasList(app?.[aliasType.field]).forEach((o) =>
        distinctOwners.add(o.toLowerCase())
      );
    });
  });

  const owners = [...distinctOwners];
  await mapWithConcurrency(
    owners,
    async (owner) => {
      // Direct ownership needs no PAPI call.
      if (owner === me) {
        matched.add(owner);
        return;
      }
      const chain = await getSupervisorChain(owner, cache);
      if (chain && chain.split(",").includes(me)) {
        matched.add(owner);
      }
    },
    PAPI_LOOKUP_CONCURRENCY
  );

  return matched;
};

/**
 * Synchronous membership check: does any of the app's owners (across all
 * ownership fields) belong to the pre-resolved owner-match set?
 */
const appMatchesOwnerSet = (app, ownerMatches) =>
  OWNERSHIP_ALIAS_TYPES.some((aliasType) =>
    toAliasList(app?.[aliasType.field]).some((o) =>
      ownerMatches.has(o.toLowerCase())
    )
  );

/**
 * Streaming variant of `filterMyPublishedApps`. Evaluates every app
 * concurrently and invokes `onMatch(app)` as soon as each qualifying app
 * resolves, so callers can render results progressively instead of waiting for
 * the whole batch. Input order is not guaranteed (matches arrive as they
 * resolve); sort in the callback if a stable order is needed.
 *
 * @param {Object[]} apps       apps from fetchApps
 * @param {string}   userAlias  current user's alias
 * @param {Function} onMatch    called with each qualifying app as it resolves
 * @param {Object}   cache      optional supervisor-chain cache to reuse
 * @param {Function} onProgress optional (resolvedOwners, totalOwners) reporter,
 *                              invoked as each distinct owner is resolved — use
 *                              it to drive a progress bar. Called once up front
 *                              with (0, total) so callers know the denominator.
 * @returns {Promise<void>} resolves once every app has been evaluated
 */
export const streamMyPublishedApps = async (
  apps = [],
  userAlias,
  onMatch,
  cache = {},
  onProgress
) => {
  const me = (userAlias || "").toLowerCase();
  if (!me || apps.length === 0) return;

  // Resolve the DISTINCT owner universe once (bounded concurrency), and as each
  // owner is found to belong to the user, emit every not-yet-emitted app owned
  // by that owner. This keeps the progressive/streaming feel while doing only
  // one PAPI lookup per distinct owner (not per app).
  const emitted = new Set();
  const emitAppsForOwner = (owner) => {
    if (typeof onMatch !== "function") return;
    apps.forEach((app) => {
      const appId = app?.app_id;
      if (emitted.has(appId)) return;
      const ownsIt = OWNERSHIP_ALIAS_TYPES.some((aliasType) =>
        toAliasList(app?.[aliasType.field]).some((o) => o.toLowerCase() === owner)
      );
      if (ownsIt) {
        emitted.add(appId);
        onMatch(app);
      }
    });
  };

  const distinctOwners = new Set();
  apps.forEach((app) => {
    OWNERSHIP_ALIAS_TYPES.forEach((aliasType) => {
      toAliasList(app?.[aliasType.field]).forEach((o) =>
        distinctOwners.add(o.toLowerCase())
      );
    });
  });

  // Progress is measured against the distinct-owner universe: one unit per
  // owner resolved. Report the denominator up front so the bar has a total.
  const totalOwners = distinctOwners.size;
  let resolvedOwners = 0;
  const reportProgress = () => {
    if (typeof onProgress === "function") onProgress(resolvedOwners, totalOwners);
  };
  reportProgress();

  await mapWithConcurrency(
    [...distinctOwners],
    async (owner) => {
      if (owner === me) {
        emitAppsForOwner(owner);
        resolvedOwners += 1;
        reportProgress();
        return;
      }
      const chain = await getSupervisorChain(owner, cache);
      if (chain && chain.split(",").includes(me)) {
        emitAppsForOwner(owner);
      }
      resolvedOwners += 1;
      reportProgress();
    },
    PAPI_LOOKUP_CONCURRENCY
  );
};

// ---------------------------------------------------------------------------
// Unpublished (automated) apps — owner-based partitioning for My Page.
//
// Unpublished AI apps come from the automated-apps store (not fetchApps) and
// have a single `app_owner` alias. We reuse the exact same supervisor-chain
// technique: one cached lookup per distinct owner alias.
//   - "mine"  : app_owner === me (direct ownership)
//   - "team"  : me is in the owner's supervisor chain (a direct/indirect report)
// ---------------------------------------------------------------------------

/**
 * The primary owner alias of an automated/unpublished app.
 * `app_owner` may be a string or an array; we take the first non-empty entry.
 */
export const getUnpublishedAppOwner = (app) => {
  const owners = toAliasList(app?.app_owner);
  return owners.length > 0 ? owners[0] : "";
};

/**
 * Split a list of unpublished apps into the current user's own apps and their
 * team's apps (owned by a direct/indirect report), using one cached
 * supervisor-chain lookup per distinct owner alias.
 *
 * @param {Object[]} apps       unpublished apps (each with an `app_owner`)
 * @param {string}   userAlias  current user's alias
 * @param {Object}   cache      optional supervisor-chain cache to reuse
 * @returns {Promise<{ mine: Object[], team: Object[] }>}
 */
export const partitionUnpublishedByReports = async (
  apps = [],
  userAlias,
  cache = {}
) => {
  const me = (userAlias || "").toLowerCase();
  const result = { mine: [], team: [] };
  if (!me || apps.length === 0) return result;

  await Promise.all(
    apps.map(async (app) => {
      const owner = getUnpublishedAppOwner(app);
      if (!owner) return;
      if (owner.toLowerCase() === me) {
        result.mine.push(app);
        return;
      }
      const chain = await getSupervisorChain(owner, cache);
      if (chain && chain.split(",").includes(me)) {
        result.team.push(app);
      }
    })
  );

  return result;
};

/**
 * Group a list of unpublished team apps by their owner alias.
 *
 * @param {Object[]} apps  unpublished apps (each with an `app_owner`)
 * @returns {Object[]} array of { owner, apps } sorted by descending app count
 */
export const groupUnpublishedByOwner = (apps = []) => {
  const byOwner = new Map();
  apps.forEach((app) => {
    const owner = getUnpublishedAppOwner(app);
    if (!owner) return;
    if (!byOwner.has(owner)) byOwner.set(owner, []);
    byOwner.get(owner).push(app);
  });
  return Array.from(byOwner.entries())
    .map(([owner, ownerApps]) => ({ owner, apps: ownerApps }))
    .sort((a, b) => b.apps.length - a.apps.length);
};

/**
 * Run an async mapper over items with a bounded concurrency, preserving input
 * order in the returned array. Prevents bursts of concurrent PAPI lookups from
 * being throttled (which was leaving some names/relationships unresolved).
 *
 * @param {Array} items
 * @param {(item: any, index: number) => Promise<any>} mapper
 * @param {number} concurrency  max in-flight mapper calls
 * @returns {Promise<any[]>}
 */
export const mapWithConcurrency = async (items = [], mapper, concurrency = 5) => {
  const results = new Array(items.length);
  let cursor = 0;

  const worker = async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await mapper(items[index], index);
    }
  };

  const workers = Array.from(
    { length: Math.min(Math.max(1, concurrency), items.length || 1) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
};

// ---------------------------------------------------------------------------
// Owner-set filtering — for team-scoped views (Close Portal) where "the team"
// is a fixed set of member aliases (e.g. resolved from an LDAP group) rather
// than a supervisor chain. Synchronous set membership — no PAPI lookups.
// ---------------------------------------------------------------------------

/**
 * Filter published apps to those whose App Builder, GRC Control Owner, or
 * Process Owner is a member of the given alias set.
 *
 * @param {Object[]}    apps       apps from fetchApps
 * @param {Set<string>} memberSet  lowercased member aliases
 * @returns {Object[]} matching apps (order preserved)
 */
export const filterAppsByOwnerSet = (apps = [], memberSet) => {
  if (!memberSet || memberSet.size === 0 || apps.length === 0) return [];
  return apps.filter((app) =>
    OWNERSHIP_ALIAS_TYPES.some((aliasType) =>
      toAliasList(app?.[aliasType.field]).some((owner) =>
        memberSet.has(owner.toLowerCase())
      )
    )
  );
};

/**
 * Filter unpublished apps to those whose app_owner is a member of the alias set.
 *
 * @param {Object[]}    apps       unpublished apps (each with an `app_owner`)
 * @param {Set<string>} memberSet  lowercased member aliases
 * @returns {Object[]} matching apps (order preserved)
 */
export const filterUnpublishedByOwnerSet = (apps = [], memberSet) => {
  if (!memberSet || memberSet.size === 0 || apps.length === 0) return [];
  return apps.filter((app) => {
    const owner = getUnpublishedAppOwner(app).toLowerCase();
    return owner && memberSet.has(owner);
  });
};
